"use client"

import { useState, useEffect, useContext } from "react"
import { TextField, Button, Box, Typography, Grid, Switch, Tooltip, alpha, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, CircularProgress, Divider, Autocomplete } from "@mui/material"
import { ErpContext } from "../../context/ErpContext.js"
import Swal from "sweetalert2"

// Icons
import SaveIcon from "@mui/icons-material/Save"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import SettingsIcon from "@mui/icons-material/Settings"

const GestionEntidadAtributo = () => {
  const theme = useTheme()
  const { 
    updateEntidadAtributo, 
    isNavbarCollapsed,
    getEntidadAtributo,
    getEntidadAtributoClasificacionByIdAtributo,
    postEntidadAtributoClasificacion,
    updateEntidadAtributoClasificacion,
    deleteEntidadAtributoClasificacion,
    tipoEntidades,
    getTipoEntidad,
  } = useContext(ErpContext)

  // NUEVO estado para tipo de entidad seleccionado
  const [tipoEntidadSeleccionado, setTipoEntidadSeleccionado] = useState(null)
  const [atributos, setAtributos] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Estados para el modal de opciones
  const [modalOpcionesOpen, setModalOpcionesOpen] = useState(false)
  const [atributoSeleccionado, setAtributoSeleccionado] = useState(null)
  const [opciones, setOpciones] = useState([])
  const [nuevaOpcion, setNuevaOpcion] = useState("")
  const [editandoOpcion, setEditandoOpcion] = useState(null)
  const [editandoOpcionTexto, setEditandoOpcionTexto] = useState("")
  const [cargandoOpciones, setCargandoOpciones] = useState(false)
  
  // Estados para el modal de confirmación de eliminación
  const [modalConfirmacionOpen, setModalConfirmacionOpen] = useState(false)
  const [opcionAEliminar, setOpcionAEliminar] = useState(null)

  // Cargar tipos de entidad al montar
  useEffect(() => {
    getTipoEntidad()
  }, [])

  // Cargar o crear atributos cuando se selecciona tipo
  const cargarOCrearAtributos = async (idTipoEntidad) => {
    try {
      // Intentar cargar existentes usando la función del contexto
      const data = await getEntidadAtributo(idTipoEntidad)
      
      // Si no hay registros, crear automáticamente los 10 atributos en BD
      if (!data || data.length === 0) {
        // Preparar los 10 atributos con valores por defecto
        const atributosPorDefecto = []
        for (let i = 1; i <= 10; i++) {
          atributosPorDefecto.push({
            idEntidadAtributo: i,
            idTipoEntidad: idTipoEntidad,
            descripcion: 'Título',  // Valor por defecto
            eliminado: i > 3  // Primeros 3 activos, resto inactivos
          })
        }
        
        // Crear automáticamente en la base de datos
        try {
          await updateEntidadAtributo({
            idTipoEntidad: idTipoEntidad,
            atributos: atributosPorDefecto
          })
          
          // Recargar los atributos recién creados
          const dataCreada = await getEntidadAtributo(idTipoEntidad)
          setAtributos(dataCreada || atributosPorDefecto)
        } catch (errorCrear) {
          console.error('Error al crear atributos automáticamente:', errorCrear)
          // Si falla la creación, mostrar los placeholders en memoria
          setAtributos(atributosPorDefecto)
        }
      } else {
        // Si hay registros, completar hasta 10 con placeholders si faltan
        const atributosCompletos = []
        for (let i = 1; i <= 10; i++) {
          const existente = data.find(a => a.idEntidadAtributo === i)
          if (existente) {
            atributosCompletos.push(existente)
          } else {
            // Crear placeholder para los que no existen (solo en memoria, no en BD)
            atributosCompletos.push({
              idEntidadAtributo: i,
              idTipoEntidad: idTipoEntidad,
              descripcion: '',
              eliminado: true
            })
          }
        }
        setAtributos(atributosCompletos)
      }
    } catch (error) {
      console.error('Error al cargar atributos:', error)
      // Incluso si hay error, mostrar los 10 atributos vacíos
      const atributosVacios = []
      for (let i = 1; i <= 10; i++) {
        atributosVacios.push({
          idEntidadAtributo: i,
          idTipoEntidad: idTipoEntidad,
          descripcion: '',
          eliminado: i > 3
        })
      }
      setAtributos(atributosVacios)
    }
  }

  // Guardar cambios (crear/actualizar)
  const submitEntidadAtributo = async (e) => {
    e.preventDefault()
    
    if (!tipoEntidadSeleccionado) {
      showAlert('Debe seleccionar un tipo de entidad', 'warning')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      // Enviar todos los atributos (se crearán los que no existan)
      await updateEntidadAtributo({
        idTipoEntidad: tipoEntidadSeleccionado.id,
        atributos: atributos
      })
      
      showAlert('Atributos guardados exitosamente', 'success')
      
      // Recargar
      await cargarOCrearAtributos(tipoEntidadSeleccionado.id)
    } catch (error) {
      console.error('Error:', error)
      showAlert('Error al guardar atributos', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Abrir modal de opciones
  const abrirModalOpciones = async (atributo) => {
    if (!tipoEntidadSeleccionado) return
    
    setAtributoSeleccionado(atributo)
    setCargandoOpciones(true)
    
    try {
      const opciones = await getEntidadAtributoClasificacionByIdAtributo(
        atributo.idEntidadAtributo,
        tipoEntidadSeleccionado.id  // NUEVO parámetro
      )
      setOpciones(opciones || [])
    } catch (error) {
      console.error('Error:', error)
      setOpciones([])
    } finally {
      setCargandoOpciones(false)
    }
    
    setModalOpcionesOpen(true)
  }

  // Crear nueva opción
  const handleCrearOpcion = async () => {
    if (!nuevaOpcion.trim()) {
      showAlert('Debe ingresar una descripción', 'warning')
      return
    }
    
    try {
      await postEntidadAtributoClasificacion({
        idEntidadAtributo: atributoSeleccionado.idEntidadAtributo,
        idTipoEntidad: tipoEntidadSeleccionado.id,  // NUEVO
        descripcion: nuevaOpcion
      })
      
      setNuevaOpcion('')
      
      // Recargar opciones
      const opcionesActualizadas = await getEntidadAtributoClasificacionByIdAtributo(
        atributoSeleccionado.idEntidadAtributo,
        tipoEntidadSeleccionado.id
      )
      setOpciones(opcionesActualizadas || [])
      
      showAlert('Opción creada exitosamente', 'success')
    } catch (error) {
      console.error('Error:', error)
      showAlert('Error al crear opción', 'error')
    }
  }

  // Actualizar opción
  const handleActualizarOpcion = async (id, descripcion, eliminado) => {
    try {
      await updateEntidadAtributoClasificacion(id, descripcion, eliminado)
      
      // Recargar opciones
      const opcionesActualizadas = await getEntidadAtributoClasificacionByIdAtributo(
        atributoSeleccionado.idEntidadAtributo,
        tipoEntidadSeleccionado.id
      )
      setOpciones(opcionesActualizadas || [])
      
      setEditandoOpcion(null)
      setEditandoOpcionTexto('')
      
      showAlert('Opción actualizada exitosamente', 'success')
    } catch (error) {
      console.error('Error:', error)
      showAlert('Error al actualizar opción', 'error')
    }
  }

  // Abrir modal de confirmación para eliminar opción
  const handleEliminarOpcion = (id) => {
    setOpcionAEliminar(id)
    setModalConfirmacionOpen(true)
  }

  // Confirmar eliminación
  const confirmarEliminacion = async () => {
    if (!opcionAEliminar) return
    
    try {
      await deleteEntidadAtributoClasificacion(opcionAEliminar)
      
      // Recargar opciones
      const opcionesActualizadas = await getEntidadAtributoClasificacionByIdAtributo(
        atributoSeleccionado.idEntidadAtributo,
        tipoEntidadSeleccionado.id
      )
      setOpciones(opcionesActualizadas || [])
      
      setModalConfirmacionOpen(false)
      setOpcionAEliminar(null)
      showAlert('Opción eliminada exitosamente', 'success')
    } catch (error) {
      console.error('Error:', error)
      showAlert('Error al eliminar opción', 'error')
      setModalConfirmacionOpen(false)
      setOpcionAEliminar(null)
    }
  }

  // Función auxiliar para mostrar alertas
  const showAlert = (message, type) => {
    Swal.fire({
      text: message,
      icon: type,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      zIndex: 1400 // Mayor que el Dialog (1300)
    })
  }

  return (
    <Box>
      {/* NUEVO: Selector de tipo de entidad */}
      <Box sx={{ mb: 3 }}>
        <Autocomplete
          size="small"
          options={tipoEntidades || []}
          getOptionLabel={(option) => option.descripcion || ''}
          value={tipoEntidadSeleccionado}
          onChange={(event, newValue) => {
            setTipoEntidadSeleccionado(newValue)
            if (newValue) {
              cargarOCrearAtributos(newValue.id)
            } else {
              setAtributos([])
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tipo de Entidad"
              required
              helperText="Seleccione el tipo de entidad para gestionar sus atributos"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#5b5b5f',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#5b5b5f',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5b5b5f',
                },
              }}
            />
          )}
        />
      </Box>
      
      {/* Mostrar info y form solo si hay tipo seleccionado */}
      {tipoEntidadSeleccionado && (
        <>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: alpha("#5b5b5f", 0.05),
              p: 1,
              borderRadius: 1,
            }}
          >
            <InfoOutlinedIcon sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2" color="text.secondary">
              Gestione los 10 atributos disponibles para <strong>{tipoEntidadSeleccionado.descripcion}</strong>. 
              Active/desactive los atributos y personalice sus nombres. Los atributos activos se mostrarán en los formularios de entidades.
            </Typography>
          </Box>
          
          <form onSubmit={submitEntidadAtributo}>
            <Grid container spacing={2}>
              {atributos.map((atributo, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Box
                    sx={{
                      p: 2,
                      border: atributo.eliminado ? "2px dashed #d0d0d0" : "2px solid #5b5b5f",
                      borderRadius: 2,
                      backgroundColor: atributo.eliminado ? alpha("#f5f5f5", 0.5) : "white",
                      transition: "all 0.3s ease",
                      opacity: atributo.eliminado ? 0.6 : 1,
                      "&:hover": {
                        boxShadow: atributo.eliminado ? "none" : "0 4px 8px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="caption" sx={{ color: "#5b5b5f", fontWeight: 600 }}>
                        Atributo {atributo.idEntidadAtributo}
                      </Typography>
                      <Tooltip title={atributo.eliminado ? "Activar" : "Desactivar"}>
                        <Switch
                          checked={!atributo.eliminado}
                          onChange={(e) => {
                            const newAtributos = [...atributos]
                            newAtributos[index].eliminado = !e.target.checked
                            setAtributos(newAtributos)
                          }}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#5b5b5f",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#5b5b5f",
                            },
                          }}
                        />
                      </Tooltip>
                    </Box>

                    <TextField
                      fullWidth
                      size="small"
                      label={`Nombre del atributo ${atributo.idEntidadAtributo}`}
                      placeholder="Titulo"  
                      value={atributo.descripcion || ''}
                      onChange={(e) => {
                        const newAtributos = [...atributos]
                        newAtributos[index].descripcion = e.target.value
                        setAtributos(newAtributos)
                      }}
                      disabled={atributo.eliminado}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#5b5b5f",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#5b5b5f",
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#5b5b5f",
                        },
                      }}
                    />

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Tooltip title="Gestionar opciones de clasificación">
                        <span>
                          <IconButton 
                            onClick={() => abrirModalOpciones(atributo)}
                            disabled={atributo.eliminado}
                            size="small"
                            sx={{
                              color: atributo.eliminado ? "#d0d0d0" : "#5b5b5f",
                              "&:hover": {
                                backgroundColor: alpha("#5b5b5f", 0.1),
                              },
                            }}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting}
                sx={{
                  backgroundColor: "#5b5b5f",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: alpha("#5b5b5f", 0.8),
                  },
                  "&:disabled": {
                    backgroundColor: "#d0d0d0",
                  },
                }}
              >
                {isSubmitting ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </Box>
          </form>
        </>
      )}
      
      {/* Modal de gestión de opciones */}
      <Dialog
        open={modalOpcionesOpen}
        onClose={() => setModalOpcionesOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            zIndex: 1300 // z-index del Dialog
          }
        }}
        BackdropProps={{
          sx: {
            zIndex: 1299 // z-index del backdrop
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#5b5b5f", color: "white", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6">
              Opciones: {atributoSeleccionado?.descripcion || `Atributo ${atributoSeleccionado?.idEntidadAtributo}`}
            </Typography>
            <Typography variant="caption">
              {tipoEntidadSeleccionado?.descripcion}
            </Typography>
          </Box>
          <IconButton onClick={() => setModalOpcionesOpen(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          {/* Crear nueva opción */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: alpha("#5b5b5f", 0.05), borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "#5b5b5f", fontWeight: 600 }}>
              Nueva Opción
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Descripción de la opción"
                value={nuevaOpcion}
                onChange={(e) => setNuevaOpcion(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleCrearOpcion()
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5b5b5f",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5b5b5f",
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleCrearOpcion}
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#5b5b5f",
                  "&:hover": {
                    backgroundColor: alpha("#5b5b5f", 0.8),
                  },
                }}
              >
                Agregar
              </Button>
            </Box>
          </Box>

          {/* Lista de opciones existentes */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: "#5b5b5f", fontWeight: 600 }}>
            Opciones Existentes
          </Typography>
          
          {cargandoOpciones ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress sx={{ color: "#5b5b5f" }} />
            </Box>
          ) : opciones.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 3, color: "text.secondary" }}>
              <Typography variant="body2">
                No hay opciones creadas aún
              </Typography>
            </Box>
          ) : (
            <List>
              {opciones.map((opcion) => (
                <ListItem
                  key={opcion.id}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: "white",
                  }}
                >
                  {editandoOpcion === opcion.id ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={editandoOpcionTexto}
                        onChange={(e) => setEditandoOpcionTexto(e.target.value)}
                        autoFocus
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleActualizarOpcion(opcion.id, editandoOpcionTexto, opcion.eliminado)}
                        sx={{ color: "green" }}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditandoOpcion(null)
                          setEditandoOpcionTexto('')
                        }}
                        sx={{ color: "red" }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={opcion.descripcion}
                        primaryTypographyProps={{
                          style: {
                            textDecoration: opcion.eliminado ? "line-through" : "none",
                            color: opcion.eliminado ? "#999" : "inherit",
                          },
                        }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Editar">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => {
                              setEditandoOpcion(opcion.id)
                              setEditandoOpcionTexto(opcion.descripcion)
                            }}
                            sx={{ mr: 1, color: "#5b5b5f" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleEliminarOpcion(opcion.id)}
                            sx={{ color: "#d32f2f" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setModalOpcionesOpen(false)} sx={{ color: "#5b5b5f" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación para eliminar opción */}
      <Dialog
        open={modalConfirmacionOpen}
        onClose={() => {
          setModalConfirmacionOpen(false)
          setOpcionAEliminar(null)
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            zIndex: 1400 // Mayor que el Dialog de opciones (1300)
          }
        }}
        BackdropProps={{
          sx: {
            zIndex: 1399 // Mayor que el backdrop del Dialog de opciones (1299)
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#d32f2f", color: "white", display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" component="span">
            ¿Está seguro?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            Esta acción no se puede deshacer. ¿Desea eliminar esta opción?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setModalConfirmacionOpen(false)
              setOpcionAEliminar(null)
            }}
            sx={{ color: "#5b5b5f" }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={confirmarEliminacion}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default GestionEntidadAtributo

